import { lazy, Suspense, useEffect } from 'react';
import { createRootRouteWithContext, useRouterState } from '@tanstack/react-router';
import type { QueryClient } from '@tanstack/react-query';
import { Flex, Image, Link, Text, VStack } from '@chakra-ui/react';
import { Outlet } from '@tanstack/react-router';
import { TAuthContext } from '@/context/auth';
import { BACKGROUND_COLOR } from '@/constants/ui';
import NotFoundPage from '@/components/templates/NotFoundPage';
import isDebug from '@/utils/isDebug';
import ErrorBoundaryComponent from '@/components/templates/ErrorBoundaryComponent';
import * as Sentry from '@sentry/react';
import useIsWeb from '@/hooks/useIsWeb';
import { formatPageTitle } from '@/constants/stringVars';
import logoVariants from '@/assets/img/logo';
import { background } from '@/theme/colors/palettes';
import svgFiles from '@/assets/svg/svgFiles';
import { DashboardTabsProvider } from '@/components/molecules/Header/DashboardTabsHost';

const TanStackRouterDevtools =
  import.meta.env.MODE === 'production'
    ? () => null // Render nothing in production
    : lazy(() =>
        // Lazy load in development
        import('@tanstack/react-router-devtools').then((res) => ({
          default: res.TanStackRouterDevtools,
        })),
      );

export const Route = createRootRouteWithContext<{
  queryClient: QueryClient;
  auth: TAuthContext | null;
}>()({
  component: RootComponent,
  notFoundComponent: NotFoundPage,
  errorComponent: ErrorBoundaryComponent,
  onError: () => {},
  onCatch: () => {},
});

function RootComponent() {
  const isWeb = useIsWeb();

  // Sets the browser tab title from the deepest matched route that declares a
  // `staticData.title`. Pages with data-driven titles (e.g. content detail)
  // override `document.title` themselves after their data loads.
  const pageTitle = useRouterState({
    select: (state) => [...state.matches].reverse().find((match) => match.staticData?.title)?.staticData.title,
  });

  useEffect(() => {
    document.title = formatPageTitle(pageTitle);
  }, [pageTitle]);

  const mobileWeeklyReport = useRouterState({
    select: ({ location }) =>
      location.pathname.startsWith('/peer-support/weekly-report') ||
      ((location.pathname.startsWith('/sign-in') ||
        location.pathname.startsWith('/onboarding') ||
        location.pathname === '/eula/update') &&
        typeof location.search.linkBack === 'string' &&
        location.search.linkBack.startsWith('/peer-support/weekly-report')),
  });

  if (!isWeb && !mobileWeeklyReport) {
    return (
      <VStack height={'100vh'} justifyContent={'center'} gap={8} backgroundColor={background.lightBlue}>
        <Image src={logoVariants.purple252x80} width={170} />

        <Text variant={'urbanistSemiBoldRegular'} textAlign={'center'} fontSize={'18px'} lineHeight={'20px'} px={8}>
          To continue using MyOmnia please download or open the application by clicking on the related store buttons
          below.
        </Text>

        <VStack gap={8}>
          <Link display={'contents'} href={'https://apps.apple.com/app/6748853118'} fontWeight={600} target="_blank">
            <Image src={svgFiles.appStoreBadge} alt="Download on the App Store" width={170} />
          </Link>

          <Link
            display={'contents'}
            href={'https://play.google.com/store/apps/details?id=com.myomnia.app'}
            fontWeight={600}
            target="_blank"
          >
            <Image src={svgFiles.playStoreBadge} alt="Get it on Google Play" width={170} />
          </Link>
        </VStack>
      </VStack>
    );
  }

  return (
    <Sentry.ErrorBoundary fallback={ErrorBoundaryComponent} onError={() => {}}>
      <DashboardTabsProvider>
        <Flex backgroundColor={BACKGROUND_COLOR} justifyContent={'center'}>
          <Outlet />
        </Flex>
      </DashboardTabsProvider>
      {isDebug && (
        <Suspense>
          <TanStackRouterDevtools />
        </Suspense>
      )}
    </Sentry.ErrorBoundary>
  );
}
